/**
 * SubmissionReview Component
 * Master-detail layout for reviewing submissions
 */

import React, { useState, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Divider
} from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { SubmissionList } from './SubmissionList';
import { SubmissionDetail } from './SubmissionDetail';
import { SubmissionActions } from './SubmissionActions';
import { useSubmission } from '../../hooks/useSubmissions';

export const SubmissionReview = () => {
  const [selectedId, setSelectedId] = useState(null);
  const queryClient = useQueryClient();

  // Fetch selected submission for actions
  const { data: selectedSubmission } = useSubmission(selectedId);

  const handleSelectSubmission = (id) => {
    setSelectedId(id);
  };

  const handleActionComplete = useCallback(() => {
    // Refetch both the submission and the list
    queryClient.invalidateQueries({ queryKey: ['submissions'] });
    if (selectedId) {
      queryClient.invalidateQueries({ queryKey: ['submission', selectedId] });
    }
  }, [queryClient, selectedId]);

  return (
    <Box>
      {/* Header */}
      <Typography variant="h4" sx={{ mb: 3 }}>
        Submission Review
      </Typography>

      {/* Master-Detail Layout */}
      <Box sx={{ display: 'flex', gap: 3, height: 'calc(100vh - 180px)' }}>
        {/* Master: Submission List */}
        <Paper sx={{ width: '40%', minWidth: 400, overflow: 'auto', p: 2 }}>
          <SubmissionList
            onSelectSubmission={handleSelectSubmission}
            selectedId={selectedId}
          />
        </Paper>

        {/* Detail: Submission Detail + Actions */}
        <Paper sx={{ flex: 1, overflow: 'auto', p: 2 }}>
          {selectedId ? (
            <Box>
              {/* Actions Panel */}
              {selectedSubmission && (
                <Box sx={{ mb: 2, p: 2, bgcolor: 'primary.50', borderRadius: 1 }}>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    Review Actions
                  </Typography>
                  <SubmissionActions
                    submission={selectedSubmission}
                    onActionComplete={handleActionComplete}
                  />
                </Box>
              )}

              <Divider sx={{ mb: 2 }} />

              {/* Detail View */}
              <SubmissionDetail submissionId={selectedId} />
            </Box>
          ) : (
            <Box sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              height: '100%'
            }}>
              <Typography color="text.secondary">
                Select a submission from the list to view details
              </Typography>
            </Box>
          )}
        </Paper>
      </Box>
    </Box>
  );
};
